import { appContext } from "../../../../AppContext";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import Helper from "../../../../Common/Helper";
import { VirtualTreeView } from "./DataModels";
import { Guid, PagedResponse } from "@iconics/core";
import { JSX } from "react";
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, LayoutContainer, LayoutContent, RightButtons, TickSymbol } from "@iconics/components";
import { VirtualTreeViewImportDialog } from "./VirtualTreeViewImportDialog";
import { IOntology } from "./DataModels";
import { ApiConstants } from "@iconics/core";

class ListVirtualTreeViews extends IbssPage<IProps, IState>
{
    private languageValuesHelper: LanguageValuesHelper;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state = {
            ontologies: [],
            gridRefreshToken: Guid.new().toString(),
            showImportDialog: false,
            canCreate: false,
            canImport: false,
            gridData: { rows: [] },
        };
    }

    public async componentDidMount(): Promise<void>
    {
        const labels = appContext().labels;
        this.pageTitle = labels.Platform_VirtualTreeView_PageTitle;
        
        await this.loadOntologies();

        this.setState({
            canCreate: appContext().localStorageProvider.hasRight('DATAMODEL.VirtualTreeView.Create'),
            canImport: appContext().localStorageProvider.hasRight('API.VirtualTreeviews.Import')
        });
    }
    
    private async loadOntologies(): Promise<void>
    {
        try
        {
            const ontologies = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.get<PagedResponse<IOntology[]>>({
                select: 'Name,IsEnabled,ID',
                tenancy: appContext().tenantId,
                top: ApiConstants.largeODataTop,
            });
            this.setState({ ontologies: ontologies.value });
        }
        catch
        {
        }
    }

    private handleImportDialogClose(success: boolean): void 
    {
        if (success)
        {
            this.setState({ showImportDialog: false, gridRefreshToken: Guid.new.toString() });
        }
        else
        {
            this.setState({ showImportDialog: false });
        }
    }

    private async handleRowsRequest(request: IGridRowsRequest<IVirtualTreeView>): Promise<IGridRowsResponse<IVirtualTreeView>>
    {
        const virtualTreeViews = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.virtualTreeViews.get<PagedResponse<VirtualTreeView[]>>({
            select: VirtualTreeView,
            skipToken: request.skipToken,
            tenancy: appContext().tenantId,
            top: request.take,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        return {
            rows: virtualTreeViews.value.map(i => ({
                id: i.Id,
                name: i.Name,
                displayName: this.languageValuesHelper.fromString(i.DisplayName),
                description: i.Description,
                definition: i.Definition,
                ontologyId: i.OntologyId,
                isEnabled: i.IsEnabled,
                eTag: i["@odata.etag"],
            } as IVirtualTreeView)),
            nextSkipToken: virtualTreeViews.skipToken
        };
    }

    public render(): JSX.Element
    {
        const labels = appContext().labels;

        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <Card
                            title={labels.Platform_VirtualTreeView_ListTitle}
                            subTitle={labels.Platform_VirtualTreeView_ListSubTitle}
                            icon={<SerendieSymbol name='layout-row' />}
                        >
                            <GridContainer>
                                <GridItem>
                                    <ButtonBar>
                                        <RightButtons>
                                            <Button
                                                size='small'
                                                styleType='ghost'
                                                leftIcon={<SerendieSymbol name='plus' />}
                                                onClick={() => this.props.navigate(`/admin/virtual-tree-views/new`)}
                                                disabled={!this.state.canCreate}
                                            >
                                                {labels.Platform_CommonAction_Add}
                                            </Button>
                                            <Button
                                                size='small'
                                                styleType='ghost'
                                                leftIcon={<SerendieSymbol name='file-upload' />}
                                                onClick={() => this.setState({ showImportDialog: true })}
                                                disabled={!this.state.canImport}
                                            >
                                                {labels.Platform_CommonAction_Import}
                                            </Button>
                                        </RightButtons>
                                    </ButtonBar>
                                </GridItem>
                                <GridItem>
                                    <DataGrid
                                        columns={[
                                            {
                                                columnId: Helper.nameOf<VirtualTreeView>("Name"),
                                                title: labels.VirtualTreeView_Name,
                                                getCellValue: i => i.name,
                                                columnWidth: 30,
                                                sortable: true,
                                            },
                                            {
                                                columnId: Helper.nameOf<VirtualTreeView>("DisplayName"),
                                                title: labels.VirtualTreeView_DisplayName,
                                                getCellValue: i => this.languageValuesHelper.getValue(i.displayName),
                                                columnWidth: 30,
                                                sortable: true,
                                            },
                                            {
                                                columnId: Helper.nameOf<VirtualTreeView>("Description"),
                                                title: labels.VirtualTreeView_Description,
                                                getCellValue: i => i.description,
                                                columnWidth: 30,
                                                sortable: true,
                                            },
                                            {
                                                columnId: Helper.nameOf<VirtualTreeView>("IsEnabled"),
                                                title: labels.VirtualTreeView_IsEnabled,
                                                getCellValue: i => i.isEnabled,
                                                template: i => <TickSymbol value={i.value as boolean} />,
                                                columnWidth: 10,
                                                sortable: true,
                                            },
                                        ]}
                                        data={this.state.gridData}
                                        rowId={i => i.id.toString()}
                                        onChange={i => this.setState({ gridData: i })}
                                        onRowsRequest={i => this.handleRowsRequest(i)}
                                        refreshToken={this.state.gridRefreshToken}
                                        onCellClicked={i => this.props.navigate(`/admin/virtual-tree-views/${i.id}/edit`)}
                                    />
                                </GridItem>
                            </GridContainer>
                        </Card>
                    </LayoutContent>
                </LayoutContainer>
                <VirtualTreeViewImportDialog
                    open={this.state.showImportDialog}
                    ontologies={this.state.ontologies.map(o => ({ value: o.Id, label: o.Name }))}
                    onClose={(success) => this.handleImportDialogClose(success)}
                />
            </>
        );
    }
}

export default withRouting(ListVirtualTreeViews);

export interface IProps extends IRouterProps { }

export interface IState
{
    ontologies: IOntology[];
    showImportDialog: boolean;
    canImport: boolean;
    canCreate: boolean;
    gridData: IGridData<IVirtualTreeView>;
    gridRefreshToken: string;
}

export interface IVirtualTreeView
{
    id: number;
    name: string;
    displayName: ILanguageValues;
    description: string;
    definition: string;
    ontologyId: string;
    isEnabled: boolean;
    eTag: string;
}
